import { useEffect, useRef, useState } from "react";
import PropertyGallery from "../../components/PropertyGallery";
import { useNavigate, Link } from "react-router-dom";
import { Home, ArrowRight, LogOut, Sun, Building, Bookmark, X as XIcon, Calendar, ExternalLink, Check, Phone, Mail, User, FileText, Download, Signature, Package, MessageSquare, Send, House, Loader2, Eye } from "lucide-react";
import DocPreviewModal from "../../components/DocPreviewModal";
import { toast } from "sonner";
import { portal, portalTokenClear, portalTokenGet } from "../../lib/api";
import { applyBranding } from "../../lib/auth";
import ClientWelcomeTour from "../../components/ClientWelcomeTour";
import ThemeToggle from "../../components/ThemeToggle";
import { MortgageCalculatorButton } from "../../components/MortgageCalculator";
import MlsIframeWithFallback from "../../components/MlsIframeWithFallback";
import ClientAgentBubble from "../../components/ClientAgentBubble";
import ImpersonationBanner, { isImpersonating } from "../../components/ImpersonationBanner";
import { SignatureChip } from "../../components/SignatureChip";

const STATUS_LABEL = {
  pre_offer: "Pre-offer", offer_out: "Offer out", under_contract: "Under contract",
  inspection: "Inspection", appraisal: "Appraisal", clear_to_close: "Clear to close",
  closed: "Closed", cancelled: "Cancelled", expired: "Expired",
};

/**
 * "Your Agent" hero card — the branded intro block at the top of every
 * client-portal page.
 *
 * Rendering rule that keeps state advertising happy:
 *   The brokerage identity sits inside THIS hero, in a column of equal
 *   width to the agent's, and the brokerage NAME is rendered with the
 *   same tag (`h2`), font-family, font-size, weight, and tracking as the
 *   agent's name. If either identity is edited, they scale together.
 *
 * Anything the agent hasn't configured (license #, DBA, office address,
 * phone) is gracefully omitted rather than shown as an empty line.
 */
function YourAgentHero({ agent, disclosure }) {
  const initials = (agent.full_name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map(s => s[0].toUpperCase()).join("");

  // Pull broker identity — fall back to what the shell already knows about
  // the agent if the API disclosure payload hasn't loaded yet, so first
  // paint doesn't flash an empty right column.
  const d = disclosure || {};
  const a = d.agent || {};
  const b = d.brokerage || {};
  const rules = d.rules || {};
  const disclaimers = d.disclaimers || {};

  const agentTitle = a.title || null;
  const agentLicense = (rules.show_agent_license !== false && a.license_number)
    ? `${a.license_number}${a.license_state ? ` · ${a.license_state}` : ""}`
    : null;

  const brokerageName = b.legal_name || null;
  const brokerageDba = b.dba || null;
  const brokerageLicense = (rules.show_brokerage_license !== false && b.license_number) ? b.license_number : null;
  const addressLine1 = [b.street, b.suite].filter(Boolean).join(" ") || null;
  const addressLine2 = [b.city, [b.state, b.zip].filter(Boolean).join(" ").trim()].filter(Boolean).join(", ") || null;
  const brokeragePhone = (rules.show_phone_numbers !== false && b.phone) ? b.phone : null;
  const showAddress = rules.show_office_address !== false;

  const hasBrokerageBlock = brokerageName || (showAddress && (addressLine1 || addressLine2));

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm mb-6"
      data-testid="portal-your-agent-hero"
      aria-label="Your agent and brokerage"
    >
      {/* Branded gradient block — extends behind BOTH columns down to the
          hairline that separates the identities from the disclaimer strip.
          Every piece of type sitting on top of this block is white; only
          the disclaimer strip below stays on the plain white surface. */}
      <div
        className="relative px-4 sm:px-6 pt-6 sm:pt-8 pb-5 sm:pb-6 text-white"
        style={{ background: "linear-gradient(135deg, var(--primary-hex) 0%, var(--accent-hex) 100%)" }}
      >
        {/* Two equal-width columns on ≥sm, stacked on mobile. The `divide`
            hairline enforces the "at least as prominent" visual balance
            between agent and brokerage — neither side can dominate. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 sm:divide-x sm:divide-white/25">
          {/* Agent column */}
          <div className="flex items-start gap-4 min-w-0">
            {agent.photo_url ? (
              <img
                src={agent.photo_url}
                alt={agent.full_name}
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full border-4 border-white shadow-md object-cover shrink-0"
                data-testid="portal-agent-photo"
              />
            ) : (
              <div
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full border-4 border-white shadow-md flex items-center justify-center text-white text-2xl font-bold font-heading shrink-0 bg-white/15 backdrop-blur-sm"
                data-testid="portal-agent-photo-fallback"
                aria-hidden="true"
              >
                {initials || <User className="h-8 w-8" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/85" data-testid="portal-agent-label">Your agent</div>
              {/* Agent name — white, but keeps the SAME h2 + font-heading
                  text-2xl · font-bold · tracking-tighter classes so it
                  matches the brokerage-name element sibling exactly. */}
              <h2 className="mt-0.5 font-heading text-2xl font-bold tracking-tighter text-slate-900 break-words" data-testid="portal-agent-name">
                {agent.full_name || "Your agent"}
              </h2>
              {(agentTitle || agentLicense) && (
                <div className="mt-1 text-xs text-white/85" data-testid="portal-agent-title-license">
                  {[agentTitle, agentLicense && `Lic. #${agentLicense}`].filter(Boolean).join(" · ")}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {agent.phone && (
                  <a
                    href={`tel:${agent.phone.replace(/[^+0-9]/g, "")}`}
                    className="inline-flex items-center gap-1.5 text-white hover:text-white/80"
                    data-testid="portal-agent-phone"
                  >
                    <Phone className="h-3.5 w-3.5" /> <span className="font-medium">{agent.phone}</span>
                  </a>
                )}
                {agent.email && (
                  <a
                    href={`mailto:${agent.email}`}
                    className="inline-flex items-center gap-1.5 text-white hover:text-white/80"
                    data-testid="portal-agent-email"
                  >
                    <Mail className="h-3.5 w-3.5" /> <span className="font-medium truncate">{agent.email}</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Brokerage column — same width, same visual weight. */}
          {hasBrokerageBlock ? (
            <div className="min-w-0 sm:pl-6" data-testid="portal-brokerage-hero">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/85">Brokerage</div>
              {/* CRITICAL: brokerage name renders with the SAME element (h2)
                  AND classes as the agent name above. Any restyle to the
                  agent-name h2 must be mirrored here — do not muted-style
                  this line, do not shrink it into a footer. State
                  advertising rules require identical prominence. */}
              <h2 className="mt-0.5 font-heading text-2xl font-bold tracking-tighter text-slate-900 break-words" data-testid="portal-brokerage-name">
                {brokerageName || "—"}
              </h2>
              {(brokerageDba || brokerageLicense) && (
                <div className="mt-1 text-xs text-white/85" data-testid="portal-brokerage-license">
                  {[brokerageDba, brokerageLicense && `Lic. #${brokerageLicense}`].filter(Boolean).join(" · ")}
                </div>
              )}
              {showAddress && (addressLine1 || addressLine2) && (
                <div className="mt-2 text-sm text-white/90 leading-snug" data-testid="portal-brokerage-address">
                  {addressLine1 && <div>{addressLine1}</div>}
                  {addressLine2 && <div>{addressLine2}</div>}
                </div>
              )}
              {brokeragePhone && (
                <a
                  href={`tel:${brokeragePhone.replace(/[^+0-9]/g, "")}`}
                  className="mt-1 inline-flex items-center gap-1.5 text-sm text-white hover:text-white/80"
                  data-testid="portal-brokerage-phone"
                >
                  <Phone className="h-3.5 w-3.5" /> <span className="font-medium">{brokeragePhone}</span>
                </a>
              )}
            </div>
          ) : (
            // Empty placeholder keeps the two-column grid stable and reminds
            // agents (via the tour + Settings blocker) to complete their
            // brokerage identity.
            <div className="min-w-0 sm:pl-6 flex items-center text-xs text-white/70" data-testid="portal-brokerage-hero-empty">
              Brokerage identity pending
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer strip — sits on the white section BELOW the gradient
          block. Kept in the neutral slate palette per compliance UX brief:
          the disclaimers must read as legal footnotes, not marketing. */}
      {(disclaimers.equal_housing_notice || disclaimers.independently_owned || disclaimers.custom || disclaimers.state_required) && (
        <div className="px-4 sm:px-6 pt-3 pb-4 flex items-start gap-2 bg-white" data-testid="portal-hero-disclaimers">
          {disclaimers.equal_housing_notice && (
            <House className="h-3.5 w-3.5 mt-[2px] text-slate-400 shrink-0" aria-hidden="true" />
          )}
          <p className="text-[11px] leading-[16px] text-slate-500">
            {[
              disclaimers.independently_owned && "Each office independently owned and operated.",
              disclaimers.equal_housing_notice && "Equal Housing Opportunity.",
              disclaimers.state_required,
              disclaimers.custom,
            ].filter(Boolean).join(" ")}
          </p>
        </div>
      )}
    </section>
  );
}

export function PortalShell({ children }) {
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [client, setClient] = useState(null);
  const [brokerage, setBrokerage] = useState(null);
  const [disclosure, setDisclosure] = useState(null);

  useEffect(() => {
    if (!portalTokenGet()) { nav("/portal/expired"); return; }
    try {
      const stored = JSON.parse(localStorage.getItem("realtor_os_portal_agent") || "null");
      setAgent(stored);
      setClient(JSON.parse(localStorage.getItem("realtor_os_portal_client") || "null"));
      setBrokerage(JSON.parse(localStorage.getItem("realtor_os_portal_brokerage") || "null"));
      setDisclosure(JSON.parse(localStorage.getItem("realtor_os_portal_disclosure") || "null"));
      // Apply both brand colors so buttons + chips + progress bars + tinted
      // body wash pick up the agent's palette on the portal too.
      applyBranding(
        stored?.brand_color || localStorage.getItem("realtor_os_portal_brand"),
        stored?.accent_color,
      );
    } catch (err) {
      // Corrupt localStorage entry — fall back to defaults and warn.
      if (process.env.NODE_ENV !== "production") console.error("[PortalShell] failed to read portal state", err);
    }
    // Always refetch the disclosure from /portal/me so a saved change in
    // Settings shows up on the client's next portal load without needing
    // them to re-verify. Cheap indexed read, ~200ms.
    portal.get("/portal/me").then(r => {
      if (r.data?.disclosure) {
        setDisclosure(r.data.disclosure);
        try {
          localStorage.setItem("realtor_os_portal_disclosure", JSON.stringify(r.data.disclosure));
        } catch { /* quota exceeded — safe to skip */ }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => { portalTokenClear(); nav("/portal/expired"); };

  return (
    <div className="min-h-screen">
      <ImpersonationBanner clientName={client?.full_name} />
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-md">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/portal" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="portal-header-brand">
            {agent?.photo_url ? (
              <img src={agent.photo_url} alt={agent.full_name || ""} className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200 shrink-0" data-testid="portal-header-agent-photo" />
            ) : (
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white shrink-0" style={{ background: "var(--primary-hex)" }}>
                <Sun className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold text-slate-900 tracking-tight truncate text-sm sm:text-base">{agent?.full_name || "Your agent"}</div>
              <div className="text-[11px] sm:text-xs text-slate-500 truncate">
                {brokerage?.name ? (
                  <span data-testid="portal-brokerage-tag">
                    <span className="hidden sm:inline">Part of </span>
                    <span className="font-semibold text-slate-700">{brokerage.name}</span>
                    <span className="hidden sm:inline"> · Client portal</span>
                  </span>
                ) : "Client portal"}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900 truncate max-w-[140px]" data-testid="portal-client-name">{client?.full_name}</div>
              <div className="text-xs text-slate-500 truncate max-w-[140px]">{client?.email}</div>
            </div>
            <ThemeToggle variant="icon" testIdPrefix="portal-theme" />
            <button onClick={logout} className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100" aria-label="Log out" data-testid="portal-logout"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8">
        {agent && <YourAgentHero agent={agent} disclosure={disclosure} />}
        {children}
      </main>
      <ClientWelcomeTour />
      {/* Contact-Agent bubble — visible on every portal route, including when
          the agent is "viewing as client" so they can verify it works. Bubble
          itself no-ops the message-send when impersonating (guarded inside). */}
      {agent && <ClientAgentBubble agent={agent} />}
      {/* NOTE: The old brokerage-tile footer used to sit here. Removed
          because the employing-broker identity now lives in the header
          hero (BrokerDisclosure Full variant) at the same visual weight
          as the agent — duplicating it in a footer both violated the
          user's design brief and made the compliance block feel like an
          afterthought. */}
    </div>
  );
}

export default function PortalHome() {
  const [tab, setTab] = useState("transactions");
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Unread agent-message count for the badge on the Messages tab. Polled
  // every 30s so the client sees new messages arrive without refreshing,
  // and re-fetched whenever they click into or out of the Messages tab
  // (opening a thread marks agent messages read on the backend).
  const [unread, setUnread] = useState(0);
  // Unread agent recommendations count for the badge on the Properties tab.
  // Kept in `localStorage['portal.recs_seen_at']` so it's per-device (a
  // dedicated backend column would sync cross-device but requires a
  // migration for a tiny gain — deferred).
  const [unreadRecs, setUnreadRecs] = useState(0);

  useEffect(() => {
    portal.get("/portal/transactions").then(r => setTxs(r.data)).finally(() => setLoading(false));
     
  }, []);

  // Count how many agent-recommendations arrived after the last "seen" time.
  // Fired on mount + on tab change so switching *to* Properties clears the
  // badge immediately, and switching *away* re-arms it for the next drop.
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const { data } = await portal.get("/portal/property-feed");
        if (stopped) return;
        const seenIso = (() => {
          try { return localStorage.getItem("portal.recs_seen_at") || ""; }
          catch (e) { console.debug("localStorage read failed", e); return ""; }
        })();
        const seen = seenIso ? new Date(seenIso).getTime() : 0;
        const n = (data.recs || []).filter(r => new Date(r.created_at).getTime() > seen).length;
        setUnreadRecs(n);
      } catch (e) { console.debug("unread-recs poll failed", e); }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { stopped = true; clearInterval(id); };
  }, [tab]);

  // Landing on the Properties tab counts as "seen" — clear the badge.
  useEffect(() => {
    if (tab !== "properties") return;
    try { localStorage.setItem("portal.recs_seen_at", new Date().toISOString()); }
    catch (e) { console.debug("localStorage write failed", e); }
    setUnreadRecs(0);
  }, [tab]);

  useEffect(() => {
    let stopped = false;
    const load = () => portal.get("/portal/messages/unread-count")
      .then(({ data }) => { if (!stopped) setUnread(data?.unread || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [tab]);  // re-poll on tab change so opening/leaving Messages refreshes fast

  // Bell for the "My Documents" tab. Right now the only source of client
  // notifications is the DocuSign send flow — the agent hits Send, we
  // insert a `client_notifications` row, and the badge lights up here.
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  useEffect(() => {
    let stopped = false;
    const load = () => portal.get("/portal/notifications/unread-count")
      .then(({ data }) => { if (!stopped) setUnreadNotifs(data?.unread || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [tab]);
  // Landing on My Documents clears the badge — the client saw the doc,
  // that's the "read" signal (mirrors how Properties clears recs).
  useEffect(() => {
    if (tab !== "documents" || unreadNotifs === 0) return;
    portal.post("/portal/notifications/mark-all-read").catch(() => {});
    setUnreadNotifs(0);
  }, [tab, unreadNotifs]);

  return (
    <PortalShell>
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Your portal</h1>
          <p className="mt-1 text-sm text-slate-500">Progress, deadlines, documents, and the properties your agent has shared with you.</p>
        </div>
      </div>

      <div className="mt-6 border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          <TabBtn active={tab === "transactions"} onClick={() => setTab("transactions")} label="Transactions" icon={Home} testId="portal-tab-transactions" />
          <TabBtn active={tab === "properties"} onClick={() => setTab("properties")} label="Properties" icon={Building} testId="portal-tab-properties" badge={unreadRecs} />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} label="Messages" icon={MessageSquare} testId="portal-tab-messages" badge={unread} />
          <TabBtn active={tab === "documents"} onClick={() => setTab("documents")} label="My Documents" icon={FileText} testId="portal-tab-documents" badge={unreadNotifs} />
        </div>
      </div>

      {tab === "transactions" && (
        <div className="mt-8 space-y-4">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && txs.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="portal-empty">
              <Home className="h-10 w-10 mx-auto text-slate-300" />
              <p className="mt-3 font-heading text-lg font-semibold text-slate-900">Nothing yet</p>
              <p className="text-sm text-slate-500 mt-1">Your agent hasn't shared a transaction with you yet.</p>
            </div>
          )}
          {txs.map(t => (
            <Link key={t.id} to={`/portal/transactions/${t.id}`} className="block rounded-lg border border-slate-200 bg-white p-5 hover:border-slate-300" data-testid={`portal-tx-${t.id}`}>
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="text-xs font-medium uppercase tracking-widest text-slate-500">{t.side} · {STATUS_LABEL[t.status] || t.status}</div>
                  <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 mt-2">{t.property_address || "—"}</h2>
                  <div className="mt-3 flex flex-wrap gap-6 text-sm text-slate-700">
                    {t.purchase_price && <div><span className="text-xs uppercase tracking-widest text-slate-500">Price</span><div className="font-semibold tabular-nums">${t.purchase_price.toLocaleString()}</div></div>}
                    {t.acceptance_date && <div><span className="text-xs uppercase tracking-widest text-slate-500">Accepted</span><div className="font-semibold tabular-nums">{t.acceptance_date}</div></div>}
                    {t.close_of_escrow_date && <div><span className="text-xs uppercase tracking-widest text-slate-500">Close</span><div className="font-semibold tabular-nums">{t.close_of_escrow_date}</div></div>}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 mt-2" />
              </div>
            </Link>
          ))}
        </div>
      )}
      {tab === "properties" && <PortalPropertyFeed />}
      {tab === "messages" && <PortalMessages />}
      {tab === "documents" && <PortalDocuments />}
    </PortalShell>
  );
}

function TabBtn({ active, onClick, label, icon: Icon, testId, badge }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 ${active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
      data-testid={testId}
    >
      <Icon className="h-4 w-4" /> {label}
      {badge > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-[10px] font-bold text-white leading-none animate-pulse"
          aria-label={`${badge} unread ${badge === 1 ? "message" : "messages"}`}
          data-testid={`${testId}-badge`}
        >
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

const PASS_REASONS = [
  ["too_expensive", "Too expensive"],
  ["too_small", "Too small"],
  ["location", "Wrong location"],
  ["condition", "Condition"],
  ["no_yard", "No yard"],
  ["layout", "Layout"],
  ["hoa", "HOA"],
  ["schools", "Schools"],
  ["commute", "Commute"],
];

function PortalPropertyFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passingCtx, setPassingCtx] = useState(null); // { collectionId, propertyId }
  const [recs, setRecs] = useState([]);
  const [mlsUrl, setMlsUrl] = useState("");
  // Reaction-based bucket filter. "all" = flat, everything grouped. Chosen
  // filter re-groups the visible feed into { recommended, favorites, toured,
  // passed } sections stacked in that order (with counts).
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [feed, r, me] = await Promise.all([
        portal.get("/portal/properties").catch(() => ({ data: { collections: [], reactions: {} } })),
        portal.get("/portal/recommended-listings").catch(() => ({ data: [] })),
        portal.get("/portal/me").catch(() => ({ data: {} })),
      ]);
      setData(feed.data);
      setRecs(r.data || []);
      setMlsUrl(me.data?.agent?.mls_search_url || "");
    } finally { setLoading(false); }
  };

   
  useEffect(() => { load(); }, []);

  const toggleLike = async (id) => {
    try {
      const { data } = await portal.post(`/portal/recommended-listings/${id}/like`);
      setRecs(prev => prev.map(r => r.id === id ? { ...r, liked: data.liked } : r));
    } catch { toast.error("Try again"); }
  };
  const askShowing = async (id) => {
    try {
      await portal.post(`/portal/recommended-listings/${id}/request-showing`);
      setRecs(prev => prev.map(r => r.id === id ? { ...r, showing_requested: true } : r));
      toast.success("Showing requested — your agent has been notified.");
    } catch { toast.error("Try again"); }
  };

  const react = async (collectionId, propertyId, reaction, extra = {}) => {
    try {
      await portal.post("/portal/properties/react", { collection_id: collectionId, property_id: propertyId, reaction, ...extra });
      // Optimistic local update — mark this property as reacted so buttons dim.
      setData(d => ({ ...d, reactions: { ...(d?.reactions || {}), [propertyId]: reaction } }));
      toast.success(
        reaction === "saved" ? "Saved" :
        reaction === "showing_requested" ? "Showing requested — your agent has been notified." :
        "Got it"
      );
    } catch {
      toast.error("Try again");
    }
  };

  const logView = async (propertyId) => {
    try { await portal.post("/portal/properties/view", { property_id: propertyId }); }
    catch (e) { console.debug("portal logView failed", e?.response?.status || e?.message); }
  };

  if (loading) return <div className="mt-8 text-sm text-slate-500">Loading…</div>;

  const hasCollections = data && data.collections.length > 0;
  const hasRecs = recs.length > 0;
  const hasMls = !!mlsUrl;

  return (
    <div className="mt-6 space-y-10" data-testid="portal-properties">

      {/* Agent Recommended — horizontal swipeable carousel at the top of the
          Properties page. Hidden entirely if no URLs shared yet. */}
      {hasRecs && (
        <section data-testid="portal-recommended">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Recommended for you</h2>
            <span className="text-xs text-slate-500">{recs.length} · from your agent</span>
          </div>
          <div className="-mx-4 sm:mx-0 overflow-x-auto snap-x snap-mandatory scroll-smooth" data-testid="portal-rec-carousel">
            <div className="flex gap-4 px-4 sm:px-0 pb-2">
              {recs.map(r => (
                <div
                  key={r.id}
                  className="snap-start shrink-0 w-[85%] sm:w-[380px] rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col"
                  data-testid={`rec-card-${r.id}`}
                >
                  <div className="relative">
                    <PropertyGallery
                      photos={[r.image_url, ...(r.photos || [])].filter(Boolean)}
                      testId={`rec-gallery-${r.id}`}
                      heightClass="h-44 sm:h-52"
                    />
                    {r.price_hint && (
                      <span className="absolute top-3 left-3 rounded-full bg-slate-900/85 text-white text-xs font-bold px-3 py-1.5 z-10 pointer-events-none">
                        {r.price_hint}
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="font-semibold text-slate-900 line-clamp-2">
                      {r.title || new URL(r.url).hostname}
                    </div>
                    {r.note && (
                      <p className="mt-1.5 text-xs text-slate-600 italic line-clamp-2">"{r.note}"</p>
                    )}
                    <div className="mt-auto pt-4 flex items-center gap-2">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                        data-testid={`rec-open-${r.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </a>
                      <button
                        type="button"
                        onClick={() => toggleLike(r.id)}
                        className={`inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${r.liked ? "bg-rose-50 border-rose-200 text-rose-600" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                        title={r.liked ? "Unlike" : "Like"}
                        aria-pressed={r.liked}
                        data-testid={`rec-like-${r.id}`}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill={r.liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => askShowing(r.id)}
                        disabled={r.showing_requested}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ background: "var(--primary-hex)" }}
                        data-testid={`rec-showing-${r.id}`}
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        {r.showing_requested ? "Requested" : "Request showing"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Agent's MLS search — embedded so the client can browse without
          leaving the portal. Responsive iframe that fills the viewport
          height below the carousel. */}
      {hasMls && (
        <section data-testid="portal-mls-frame">
          <div className="mb-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Search the market</h2>
              <MortgageCalculatorButton />
            </div>
            <div className="flex items-center justify-center gap-3 flex-wrap text-center">
              <span className="text-xs font-bold text-red-600" data-testid="portal-mls-embed-note">
                Some MLS sites block being embedded. If the box stays empty, use the open in new tab link.
              </span>
              <a
                href={mlsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-red-600 hover:text-red-700 inline-flex items-center gap-1 shrink-0"
                data-testid="portal-mls-open-new-tab"
              >
                Open in new tab <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <MlsIframeWithFallback url={mlsUrl} />
          </div>
        </section>
      )}

      {/* Fallback empty state — only when the agent hasn't set up anything yet. */}
      {!hasRecs && !hasMls && !hasCollections && (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="portal-props-empty">
          <Building className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No properties shared yet</p>
          <p className="text-sm text-slate-500 mt-1">Your agent hasn't shared any properties with you. They'll show up here as soon as they do.</p>
        </div>
      )}

      {/* Grouped by reaction — Favorites / Toured / Recommended / Passed.
          Sourced by flattening every collection's properties and bucketing
          them via `data.reactions`. Filter chips at the top let the client
          jump between buckets. */}
      {hasCollections && (() => {
        const seen = new Set();
        const buckets = { recommended: [], favorites: [], toured: [], passed: [] };
        for (const c of data.collections) {
          for (const p of c.properties) {
            if (seen.has(p.id)) continue;
            seen.add(p.id);
            const rx = data.reactions[p.id];
            const ctx = { ...p, _collectionId: c.id, _collectionName: c.name };
            if (rx === "saved" || rx === "showing_requested") buckets.favorites.push(ctx);
            else if (rx === "toured") buckets.toured.push(ctx);
            else if (rx === "passed") buckets.passed.push(ctx);
            else buckets.recommended.push(ctx);
          }
        }
        const totals = {
          all: buckets.recommended.length + buckets.favorites.length + buckets.toured.length + buckets.passed.length,
          recommended: buckets.recommended.length,
          favorites: buckets.favorites.length,
          toured: buckets.toured.length,
          passed: buckets.passed.length,
        };

        const chip = (key, label) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
              filter === key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-400"
            }`}
            data-testid={`portal-filter-${key}`}
          >
            {label}
            <span className={`text-[10px] rounded-full px-1.5 ${filter === key ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{totals[key]}</span>
          </button>
        );

        const renderSection = (name, key, rows) => {
          if (filter !== "all" && filter !== key) return null;
          if (rows.length === 0) return null;
          return (
            <section key={key} data-testid={`portal-section-${key}`}>
              <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">{name}</h2>
                <span className="text-xs text-slate-500">{rows.length} {rows.length === 1 ? "property" : "properties"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {rows.map(p => (
                  <PortalPropertyCard
                    key={p.id + p._collectionId}
                    p={p}
                    reaction={data.reactions[p.id]}
                    onSave={() => react(p._collectionId, p.id, "saved")}
                    onShowing={() => react(p._collectionId, p.id, "showing_requested")}
                    onPassStart={() => setPassingCtx({ collectionId: p._collectionId, propertyId: p.id })}
                    onViewFullListing={() => logView(p.id)}
                  />
                ))}
              </div>
            </section>
          );
        };

        return (
          <div className="space-y-8" data-testid="portal-grouped-properties">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mr-1">View</span>
              {chip("all", "All")}
              {chip("recommended", "Recommended")}
              {chip("favorites", "Favorites")}
              {chip("toured", "Toured")}
              {chip("passed", "Passed")}
            </div>
            {renderSection("Recommended", "recommended", buckets.recommended)}
            {renderSection("Favorites", "favorites", buckets.favorites)}
            {renderSection("Toured", "toured", buckets.toured)}
            {renderSection("Passed", "passed", buckets.passed)}
            {totals.all === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
                No properties in your collections yet.
              </div>
            )}
          </div>
        );
      })()}

      {passingCtx && (
        <PortalPassSheet
          onClose={() => setPassingCtx(null)}
          onConfirm={(reasons, comment) => {
            react(passingCtx.collectionId, passingCtx.propertyId, "passed", { reason_codes: reasons, comment });
            setPassingCtx(null);
          }}
        />
      )}
    </div>
  );
}

function PortalPropertyCard({ p, reaction, onSave, onShowing, onPassStart, onViewFullListing }) {
  const price = p.list_price ? `$${Number(p.list_price).toLocaleString()}` : "—";
  const bbs = [p.beds && `${p.beds} bd`, p.baths && `${p.baths} ba`, p.sqft && `${Number(p.sqft).toLocaleString()} sqft`].filter(Boolean).join(" · ");
  const reacted = !!reaction;
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`portal-card-${p.id}`}>
      <div className="aspect-[16/10] bg-slate-100 relative">
        {p.hero_image_url ? (
          <img src={p.hero_image_url} alt={p.address_line1} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><Home className="h-12 w-12" /></div>
        )}
        {reaction && (
          <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold flex items-center gap-1 shadow-sm">
            <Check className="h-3 w-3" /> {reaction === "showing_requested" ? "Showing requested" : reaction === "saved" ? "Saved" : "Passed"}
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-heading font-bold text-slate-900 truncate">{p.address_line1}</div>
            <div className="text-xs text-slate-500">{[p.city, p.state].filter(Boolean).join(", ")}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-heading text-lg font-bold text-slate-900 tabular-nums">{price}</div>
            {bbs && <div className="text-[11px] text-slate-500">{bbs}</div>}
          </div>
        </div>
        {p.description && <p className="mt-2 text-xs text-slate-600 leading-relaxed line-clamp-2">{p.description}</p>}
        {p.agent_note && (
          <div className="mt-3 rounded-md border-l-2 p-2 text-xs text-slate-700 bg-slate-50" style={{ borderColor: "var(--primary-hex)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest opacity-60 mb-0.5">Note</div>
            {p.agent_note}
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          <PBtn onClick={onSave} disabled={reacted} icon={Bookmark} label="Save" testId={`portal-save-${p.id}`} />
          <PBtn onClick={onShowing} disabled={reacted} icon={Calendar} label="Showing" testId={`portal-showing-${p.id}`} primary />
          <PBtn onClick={onPassStart} disabled={reacted} icon={XIcon} label="Pass" testId={`portal-pass-${p.id}`} />
          {p.listing_url && (
            <a href={p.listing_url} target="_blank" rel="noopener noreferrer" onClick={onViewFullListing}
               className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
               data-testid={`portal-listing-${p.id}`}>
              <ExternalLink className="h-3 w-3" /> Full
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function PBtn({ onClick, disabled, icon: Icon, label, primary, testId }) {
  const base = "inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed";
  const style = primary ? { background: "var(--primary-hex)", color: "white" } : {};
  const cls = primary ? "" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${cls}`} style={style} data-testid={testId}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function PortalPassSheet({ onClose, onConfirm }) {
  const [picked, setPicked] = useState([]);
  const [comment, setComment] = useState("");
  const toggle = (code) => setPicked(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-8" role="dialog">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-xl sm:rounded-xl bg-white shadow-2xl">
        <div className="p-6">
          <h3 className="font-heading text-xl font-bold tracking-tight text-slate-900">What didn't work?</h3>
          <p className="mt-1 text-sm text-slate-500">Your agent uses this to bring you better options next time.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {PASS_REASONS.map(([code, label]) => (
              <button
                key={code}
                onClick={() => toggle(code)}
                className={`rounded-full border px-3 py-1.5 text-sm ${picked.includes(code) ? "text-white border-transparent" : "text-slate-700 bg-white border-slate-300 hover:bg-slate-50"}`}
                style={picked.includes(code) ? { background: "var(--primary-hex)" } : {}}
                data-testid={`portal-pass-reason-${code}`}
              >{label}</button>
            ))}
          </div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Anything else? (optional)" className="input mt-4 min-h-[60px]" />
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900">Cancel</button>
            <button onClick={() => onConfirm(picked, comment || null)} className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="portal-pass-confirm">
              Send feedback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── My Documents ──────────────────────────────────────────────────────────
// Flat list of every PDF/doc the agent has assigned to any transaction this
// client can see. Signed download URLs come baked in from /portal/documents so
// no extra round-trip is needed to open a file.

function fmtSize(bytes) {
  if (!bytes) return null;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Client cannot initiate e-signatures — that's strictly an agent action.
  // Documents render as read-only with status chips + a Sign now button
  // that opens an in-portal iframe modal against a freshly-minted signing
  // URL (single-use, always refreshed on click — so links never "expire").
  const [openingSign, setOpeningSign] = useState(null); // doc id currently minting a signing URL
  const [signSession, setSignSession] = useState(null); // { docId, docName, url }
  // Inline preview modal — reuses the shared agent DocPreviewModal via
  // `directUrl` so the portal doesn't need to hit an agent-only route.
  // The `download_url` we already ship in /portal/documents payload is
  // the source; expires in an hour, same as the agent side.
  const [previewDoc, setPreviewDoc] = useState(null); // { id, name, url }

  const reload = () => {
    return Promise.all([
      portal.get("/portal/documents").then(r => r.data).catch(() => []),
      portal.get("/portal/document-packages").then(r => r.data).catch(() => []),
    ]).then(([d, p]) => {
      setDocs(d);
      setPackages(p);
    });
  };

  useEffect(() => { reload().finally(() => setLoading(false)); }, []);

  // Keep the portal list in sync with server truth. When an agent
  // deletes a doc from Transactions or Client Detail, we need to drop
  // the stale row fast — poll every 15s while the tab is visible AND
  // refetch immediately on visibility/focus so a client tabbing back
  // never sees a phantom document that would 404 on click.
  useEffect(() => {
    if (loading) return;
    const refresh = () => {
      if (document.hidden) return;
      reload().catch(() => {});
    };
    const id = setInterval(refresh, 15_000);
    const onVis = () => { if (!document.hidden) refresh(); };
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", refresh);
    };
  }, [loading]);

  // Sign now → mint a FRESH signing URL every click (DocuSign recipient
  // views are single-use + short-TTL, so any URL we cached would look
  // expired). Open the fresh URL inside an in-portal iframe modal so the
  // client stays inside iKEY branding end-to-end.
  const openSigningSession = async (doc) => {
    setOpeningSign(doc.id);
    try {
      const { data } = await portal.post(`/portal/documents/${doc.id}/signing-url`);
      if (data?.signing_url) {
        // Audit BEFORE opening so the agent sees the click even if the
        // signing modal is closed before signature completes.
        portal.post(`/portal/documents/${doc.id}/signing-events`, { event: "opened" }).catch(() => {});
        setSignSession({ docId: doc.id, docName: doc.name, url: data.signing_url });
      } else {
        toast.error("Could not open signing session — please try again in a moment.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      toast.error(detail || "Could not open signing session");
    } finally { setOpeningSign(null); }
  };

  // Bridge from the DocuSign iframe → portal state.
  //   * "docusign_signed" is posted by /portal/sign-complete when the
  //     returnUrl page mounts. It carries the DocuSign event name
  //     (signing_complete, cancel, decline, session_timeout, …).
  //   * On signing_complete we close the modal, toast success, and
  //     immediately reload — the row flips to "Signed" + View signed PDF
  //     without a page refresh.
  useEffect(() => {
    const onMsg = (ev) => {
      const msg = ev?.data;
      if (msg?.type !== "docusign_signed") return;
      const event = (msg.event || "").toLowerCase();
      if (event === "signing_complete" || event === "viewing_complete") {
        setSignSession(null);
        toast.success("Signature captured — your agent has been notified.");
      } else if (event === "cancel" || event === "decline") {
        setSignSession(null);
      } else if (event === "session_timeout" || event === "ttl_expired") {
        // Silently swallow — the "Sign now" click will mint a new URL
        // when the client tries again.
        setSignSession(null);
      }
      reload();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  if (loading) return <div className="mt-8 text-sm text-slate-500">Loading…</div>;

  if (docs.length === 0 && packages.length === 0) {
    return (
      <div className="mt-8 rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="portal-docs-empty">
        <FileText className="h-10 w-10 mx-auto text-slate-300" />
        <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No documents yet</p>
        <p className="text-sm text-slate-500 mt-1">Your agent hasn't shared any files with you yet. They'll appear here as soon as they do.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6" data-testid="portal-docs-list">
      {packages.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 px-1">Packets from your agent</h3>
          {packages.map(p => (
            <div
              key={p.id}
              className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
              data-testid={`portal-pkg-${p.id}`}
            >
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center text-white shrink-0"
                style={{ background: "var(--accent-hex)" }}
                aria-hidden="true"
              >
                <Package className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {p.document_count} document{p.document_count === 1 ? "" : "s"}
                  {p.zip_size_bytes ? ` · ${fmtSize(p.zip_size_bytes)}` : ""}
                  {p.expires_at ? ` · until ${new Date(p.expires_at).toLocaleDateString()}` : ""}
                </div>
                {p.note && <div className="mt-1.5 text-xs text-slate-600 italic">"{p.note}"</div>}
              </div>
              <a
                href={p.download_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold text-white hover:opacity-95"
                style={{ background: "var(--primary-hex)" }}
                data-testid={`portal-pkg-dl-${p.id}`}
              >
                <Download className="h-3.5 w-3.5" /> Download packet
              </a>
            </div>
          ))}
        </div>
      )}
      {docs.length > 0 && packages.length > 0 && (
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 px-1">Individual documents</h3>
      )}
      <div className="space-y-3">
      {docs.map(d => (
        <div
          key={d.id}
          className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
          data-testid={`portal-doc-${d.id}`}
        >
          <div
            className="h-10 w-10 rounded-md flex items-center justify-center text-white shrink-0"
            style={{ background: "var(--primary-hex)" }}
            aria-hidden="true"
          >
            <FileText className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
              <span className="truncate">{d.name}</span>
              <SignatureChip status={d.signature_status} testId={`portal-doc-sig-${d.id}`} />
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              {d.transaction_address || "—"}
              {d.size_bytes ? ` · ${fmtSize(d.size_bytes)}` : ""}
              {d.uploaded_by_agent === false ? " · Uploaded by you" : ""}
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <a
              href={d.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
              data-testid={`portal-doc-download-${d.id}`}
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
            {/* Signature status is READ-ONLY for the client — only the agent
                can initiate the e-signature envelope. If a status is set,
                surface it as an informational chip; otherwise render nothing. */}
            {d.signature_status === "completed" && (
              <>
                <span
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800"
                  data-testid={`portal-doc-sig-status-${d.id}`}
                >
                  <Signature className="h-3.5 w-3.5" /> Signed
                </span>
                {d.signed_document_id && (
                  <a
                    href={`/api/v1/documents/${d.signed_document_id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white hover:opacity-95"
                    style={{ background: "var(--primary-hex)" }}
                    title="Open the countersigned PDF"
                    data-testid={`portal-doc-view-signed-${d.id}`}
                  >
                    <FileText className="h-3.5 w-3.5" /> View signed PDF
                  </a>
                )}
              </>
            )}
            {(d.signature_status === "sent" || d.signature_status === "delivered") && (
              <button
                type="button"
                onClick={() => openSigningSession(d)}
                disabled={openingSign === d.id}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                style={{ background: "var(--primary-hex)" }}
                title="Sign this document right here in your portal"
                data-testid={`portal-doc-open-sign-${d.id}`}
              >
                {openingSign === d.id ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…</>
                ) : (
                  <><Signature className="h-3.5 w-3.5" /> Sign now</>
                )}
              </button>
            )}
            {d.download_url && (
              <button
                type="button"
                onClick={() => setPreviewDoc({ id: d.id, name: d.name, url: d.download_url })}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                title="Preview this document inline"
                data-testid={`portal-doc-preview-${d.id}`}
              >
                <Eye className="h-3.5 w-3.5" /> Preview
              </button>
            )}
          </div>
        </div>
      ))}
      </div>
      {signSession && (
        <SigningIframeModal
          docName={signSession.docName}
          url={signSession.url}
          onClose={() => setSignSession(null)}
        />
      )}
      {previewDoc && (
        <DocPreviewModal
          docId={previewDoc.id}
          name={previewDoc.name}
          directUrl={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          testIdPrefix="portal-doc-preview-modal"
        />
      )}
    </div>
  );
}

// ── In-portal signing modal ────────────────────────────────────────────
// DocuSign's embedded recipient view supports iframe hosting when we use
// `clientUserId` — we do (`portal:{doc_id}:{email}` in ai/docusign.py).
// The iframe navigates itself to /portal/sign-complete on completion; that
// page posts `docusign_signed` up to window.parent, which we handle in the
// parent to close the modal and refresh the doc list.
function SigningIframeModal({ docName, url, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);
  // Lock body scroll while the modal is open so the client can't lose
  // the iframe by scrolling behind it on mobile.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signing-modal-title"
      data-testid="portal-sign-modal"
    >
      <div className="relative w-full max-w-5xl h-[92vh] sm:h-[88vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Sign in your portal
            </div>
            <h3 id="signing-modal-title" className="mt-0.5 font-heading text-lg font-bold text-slate-900 truncate" data-testid="portal-sign-modal-title">
              {docName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 p-1 rounded-md hover:bg-slate-100 shrink-0"
            aria-label="Close signing session"
            data-testid="portal-sign-modal-close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="relative flex-1 min-h-0 bg-slate-50">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm gap-2" data-testid="portal-sign-modal-loading">
              <Loader2 className="h-5 w-5 animate-spin" />
              Preparing your signing session…
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={url}
            title="DocuSign signing session"
            className="w-full h-full border-0"
            onLoad={() => setLoaded(true)}
            data-testid="portal-sign-iframe"
            // DocuSign embedded signing needs same-origin scripts + forms;
            // sandbox left open so the DocuSign UI runs normally.
          />
        </div>
      </div>
    </div>
  );
}


// ── Messages tab — unified inbox across every transaction the client
// has access to. Selecting a thread on the left renders the chat on the
// right; sending a message posts to the same per-tx endpoint the bubble
// uses so nothing about the existing chat behavior changes.

function PortalMessages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTxId, setActiveTxId] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    return portal.get("/portal/messages")
      .then(({ data }) => {
        setThreads(data || []);
        setActiveTxId(prev => prev || (data && data.length ? data[0].transaction_id : null));
      })
      .catch((e) => { console.debug("portal messages load failed", e?.response?.status || e?.message); });
  };
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const active = threads.find(t => t.transaction_id === activeTxId) || null;

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeTxId) return;
    setSending(true);
    try {
      await portal.post(`/portal/transactions/${activeTxId}/messages`, { body });
      setDraft("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  if (loading) return <div className="mt-8 text-sm text-slate-500">Loading messages…</div>;
  if (threads.length === 0) {
    return (
      <div className="mt-8 rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="portal-messages-empty">
        <MessageSquare className="h-10 w-10 mx-auto text-slate-300" />
        <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No conversations yet</p>
        <p className="text-sm text-slate-500 mt-1">Once your agent starts a transaction with you, your chats will show up here.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] gap-4" data-testid="portal-messages-panel">
      {/* Thread list */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100 max-h-[62vh] overflow-y-auto">
        {threads.map(t => {
          const isActive = t.transaction_id === activeTxId;
          const preview = t.messages.length
            ? t.messages[t.messages.length - 1].body
            : "No messages yet — say hi!";
          return (
            <button
              key={t.transaction_id}
              onClick={() => setActiveTxId(t.transaction_id)}
              className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${isActive ? "bg-slate-50" : ""}`}
              data-testid={`portal-thread-${t.transaction_id}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-semibold text-sm text-slate-900 truncate">{t.property_address}</div>
                {t.unread > 0 && (
                  <span className="text-[10px] font-bold text-white bg-rose-500 rounded-full px-1.5 py-px shrink-0" data-testid={`portal-thread-unread-${t.transaction_id}`}>
                    {t.unread}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{preview}</div>
            </button>
          );
        })}
      </div>

      {/* Active thread */}
      <div className="rounded-lg border border-slate-200 bg-white flex flex-col max-h-[62vh]">
        <div className="px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="font-semibold text-sm text-slate-900 truncate">{active?.property_address || "—"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Message your agent</div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="portal-messages-scroll">
          {active && active.messages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10">
              No messages yet — type below to start the conversation.
            </div>
          )}
          {active && active.messages.map(m => {
            const mine = m.sender === "client";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "text-white" : "bg-slate-100 text-slate-900"}`}
                  style={mine ? { background: "var(--primary-hex)" } : undefined}
                  data-testid={`portal-message-${m.id}`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-500"}`}>
                    {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-slate-200 p-3 flex items-center gap-2 shrink-0"
        >
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand"
            data-testid="portal-message-input"
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--primary-hex)" }}
            data-testid="portal-message-send"
          >
            <Send className="h-3.5 w-3.5" /> Send
          </button>
        </form>
      </div>
    </div>
  );
}
